// src/utils/evaluationPeriodUtils.js
//
// Central evaluation-period utility layer
// =======================================
//
// WHY THIS FILE EXISTS
// --------------------
// Many pages in your application already work with the same backend pattern:
//
//   GET ...?from=YYYY-MM-DD&to=YYYY-MM-DD
//
// The business problem is not the backend format itself.
// The UX problem is that selecting older months/weeks/years using two plain
// date inputs is slow and inconvenient.
//
// This utility file centralizes ALL date-range logic for a reusable frontend
// "evaluation period" framework.
//
// TARGET UX
// ---------
// The UI can operate in one of these modes:
//
//   - MONTH
//   - YEAR
//   - WEEK
//   - DAY
//   - MANUAL
//
// For MONTH / YEAR / WEEK / DAY:
//   - the user navigates using left/right arrows
//   - the framework computes from/to automatically
//   - the UI shows a human-readable label for the current period
//
// For MANUAL:
//   - the arrows disappear
//   - two date inputs (from / to) are shown instead
//
// IMPORTANT DESIGN DECISION
// -------------------------
// We DO NOT block future dates.
// That matches your requirement:
// the user may navigate into the future, and the report may simply return no data.
//
// NO EXTERNAL DATE LIBRARY
// ------------------------
// This implementation intentionally uses native Date only.
// That keeps the pilot lightweight and avoids adding dependencies.
//
// TIMEZONE / DST SAFETY
// ---------------------
// For pure "calendar period" navigation, local midnight can sometimes be awkward
// around DST transitions. To reduce strange edge cases, we normalize internal
// anchor dates to LOCAL NOON where possible.
//
// The final API params sent to the backend remain plain YYYY-MM-DD strings.
//

/* -------------------------------------------------------------------------- */
/* Public constants                                                           */
/* -------------------------------------------------------------------------- */

export const EVALUATION_PERIOD_TYPES = Object.freeze({
  MONTH: "MONTH",
  THREE_MONTHS: "THREE_MONTHS",
  SIX_MONTHS: "SIX_MONTHS",
  YEAR: "YEAR",
  WEEK: "WEEK",
  DAY: "DAY",
  MANUAL: "MANUAL",
});

export const EVALUATION_PERIOD_OPTIONS = Object.freeze([
  { value: EVALUATION_PERIOD_TYPES.MONTH, label: "Month" },
  { value: EVALUATION_PERIOD_TYPES.THREE_MONTHS, label: "3 months" },
  { value: EVALUATION_PERIOD_TYPES.SIX_MONTHS, label: "6 months" },
  { value: EVALUATION_PERIOD_TYPES.YEAR, label: "Year" },
  { value: EVALUATION_PERIOD_TYPES.WEEK, label: "Week" },
  { value: EVALUATION_PERIOD_TYPES.DAY, label: "Day" },
  { value: EVALUATION_PERIOD_TYPES.MANUAL, label: "Manual" },
]);

/* -------------------------------------------------------------------------- */
/* Small low-level helpers                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Build a local Date at 12:00 (noon).
 *
 * Why noon?
 * - For date-range navigation, noon is often more stable than midnight
 *   around DST boundaries.
 */
function createLocalNoonDate(year, monthIndex, day) {
  return new Date(year, monthIndex, day, 12, 0, 0, 0);
}

/**
 * Normalize any date-like input to a valid Date anchored at local noon.
 * If invalid, return the provided fallback (or "today").
 */
export function normalizeDate(dateLike, fallback = new Date()) {
  const candidate = dateLike instanceof Date ? dateLike : new Date(dateLike);
  const safe = Number.isFinite(candidate.getTime()) ? candidate : new Date(fallback);

  return createLocalNoonDate(
    safe.getFullYear(),
    safe.getMonth(),
    safe.getDate()
  );
}

/**
 * Convert a Date to YYYY-MM-DD for backend/API params and <input type="date" />.
 */
export function toDateInputValue(dateLike) {
  const d = normalizeDate(dateLike);

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Parse YYYY-MM-DD from a date input value.
 * Returns Date at local noon, or fallback if invalid.
 */
export function parseDateInputValue(input, fallback = null) {
  if (!input || typeof input !== "string") {
    return fallback ? normalizeDate(fallback) : null;
  }

  const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    return fallback ? normalizeDate(fallback) : null;
  }

  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);

  const d = createLocalNoonDate(year, monthIndex, day);
  return Number.isFinite(d.getTime()) ? d : fallback ? normalizeDate(fallback) : null;
}

/**
 * Return a new Date shifted by N days.
 */
export function addDays(dateLike, days) {
  const d = normalizeDate(dateLike);
  const copy = createLocalNoonDate(d.getFullYear(), d.getMonth(), d.getDate());
  copy.setDate(copy.getDate() + Number(days || 0));
  return normalizeDate(copy);
}

/**
 * Return a new Date shifted by N months.
 */
export function addMonths(dateLike, months) {
  const d = normalizeDate(dateLike);
  const copy = createLocalNoonDate(d.getFullYear(), d.getMonth(), d.getDate());
  copy.setMonth(copy.getMonth() + Number(months || 0));
  return normalizeDate(copy);
}

/**
 * Return a new Date shifted by N years.
 */
export function addYears(dateLike, years) {
  const d = normalizeDate(dateLike);
  const copy = createLocalNoonDate(d.getFullYear(), d.getMonth(), d.getDate());
  copy.setFullYear(copy.getFullYear() + Number(years || 0));
  return normalizeDate(copy);
}

/* -------------------------------------------------------------------------- */
/* Period boundary helpers                                                    */
/* -------------------------------------------------------------------------- */

export function startOfMonth(dateLike) {
  const d = normalizeDate(dateLike);
  return createLocalNoonDate(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(dateLike) {
  const d = normalizeDate(dateLike);
  return createLocalNoonDate(d.getFullYear(), d.getMonth() + 1, 0);
}

export function startOfYear(dateLike) {
  const d = normalizeDate(dateLike);
  return createLocalNoonDate(d.getFullYear(), 0, 1);
}

export function endOfYear(dateLike) {
  const d = normalizeDate(dateLike);
  return createLocalNoonDate(d.getFullYear(), 11, 31);
}

/**
 * ISO week starts on Monday.
 */
export function startOfIsoWeek(dateLike) {
  const d = normalizeDate(dateLike);

  // JS getDay():
  // 0 = Sunday
  // 1 = Monday
  // ...
  // 6 = Saturday
  const jsDay = d.getDay();
  const isoDay = jsDay === 0 ? 7 : jsDay; // convert Sunday 0 -> ISO 7

  return addDays(d, -(isoDay - 1));
}

export function endOfIsoWeek(dateLike) {
  return addDays(startOfIsoWeek(dateLike), 6);
}

export function startOfDay(dateLike) {
  return normalizeDate(dateLike);
}

export function endOfDay(dateLike) {
  return normalizeDate(dateLike);
}

/* -------------------------------------------------------------------------- */
/* ISO week number                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Compute ISO week number.
 *
 * We use the standard Thursday-based ISO approach.
 */
export function getIsoWeekNumber(dateLike) {
  const d = normalizeDate(dateLike);

  // Copy date
  const working = createLocalNoonDate(d.getFullYear(), d.getMonth(), d.getDate());

  // Set to Thursday of current ISO week
  const jsDay = working.getDay();
  const isoDay = jsDay === 0 ? 7 : jsDay;
  working.setDate(working.getDate() + (4 - isoDay));

  // First Thursday of the year
  const yearStart = createLocalNoonDate(working.getFullYear(), 0, 1);
  const yearStartDay = yearStart.getDay();
  const yearStartIsoDay = yearStartDay === 0 ? 7 : yearStartDay;
  yearStart.setDate(yearStart.getDate() + (4 - yearStartIsoDay));

  const diffMs = working.getTime() - yearStart.getTime();
  const diffDays = Math.round(diffMs / 86400000);

  return 1 + Math.floor(diffDays / 7);
}

/* -------------------------------------------------------------------------- */
/* Mode normalization                                                         */
/* -------------------------------------------------------------------------- */

export function normalizeEvaluationPeriodMode(mode) {
  const normalized = String(mode || "").toUpperCase();

  if (Object.values(EVALUATION_PERIOD_TYPES).includes(normalized)) {
    return normalized;
  }

  return EVALUATION_PERIOD_TYPES.MONTH;
}

/* -------------------------------------------------------------------------- */
/* Labels                                                                     */
/* -------------------------------------------------------------------------- */

const monthFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});

const dayFormatter = new Intl.DateTimeFormat("en-US", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

export function buildEvaluationPeriodLabel({ mode, fromDate, toDate, cursorDate }) {
  const safeMode = normalizeEvaluationPeriodMode(mode);

  if (safeMode === EVALUATION_PERIOD_TYPES.MANUAL) {
    return "";
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.MONTH) {
    return monthFormatter.format(cursorDate);
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.THREE_MONTHS || safeMode === EVALUATION_PERIOD_TYPES.SIX_MONTHS) {
    return `${monthFormatter.format(fromDate)} - ${monthFormatter.format(toDate)}`;
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.YEAR) {
    return String(cursorDate.getFullYear());
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.DAY) {
    return dayFormatter.format(cursorDate);
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.WEEK) {
    const weekNo = getIsoWeekNumber(cursorDate);
    return `Week ${weekNo}, ${fromDate.getFullYear()} (${toDateInputValue(fromDate)} - ${toDateInputValue(toDate)})`;
  }

  return "";
}

/* -------------------------------------------------------------------------- */
/* Default manual range                                                       */
/* -------------------------------------------------------------------------- */

/**
 * For the first MANUAL open, it is usually helpful to prefill the current month.
 */
export function buildDefaultManualRange(anchorDate = new Date()) {
  const start = startOfMonth(anchorDate);
  const end = endOfMonth(anchorDate);

  return {
    from: toDateInputValue(start),
    to: toDateInputValue(end),
  };
}

/* -------------------------------------------------------------------------- */
/* Core range builder                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Build one canonical period object from:
 * - mode
 * - cursorDate
 * - manualFrom
 * - manualTo
 *
 * Returns a frontend-friendly object:
 * {
 *   mode,
 *   isManual,
 *   showManualInputs,
 *   from,
 *   to,
 *   fromDate,
 *   toDate,
 *   displayLabel
 * }
 */
export function buildEvaluationPeriodRange({
  mode,
  cursorDate,
  manualFrom,
  manualTo,
}) {
  const safeMode = normalizeEvaluationPeriodMode(mode);
  const safeCursor = normalizeDate(cursorDate);

  let fromDate = safeCursor;
  let toDate = safeCursor;

  if (safeMode === EVALUATION_PERIOD_TYPES.MONTH) {
    fromDate = startOfMonth(safeCursor);
    toDate = endOfMonth(safeCursor);
  } else if (safeMode === EVALUATION_PERIOD_TYPES.THREE_MONTHS) {
    fromDate = startOfMonth(safeCursor);
    toDate = endOfMonth(addMonths(fromDate, 2));
  } else if (safeMode === EVALUATION_PERIOD_TYPES.SIX_MONTHS) {
    fromDate = startOfMonth(safeCursor);
    toDate = endOfMonth(addMonths(fromDate, 5));
  } else if (safeMode === EVALUATION_PERIOD_TYPES.YEAR) {
    fromDate = startOfYear(safeCursor);
    toDate = endOfYear(safeCursor);
  } else if (safeMode === EVALUATION_PERIOD_TYPES.WEEK) {
    fromDate = startOfIsoWeek(safeCursor);
    toDate = endOfIsoWeek(safeCursor);
  } else if (safeMode === EVALUATION_PERIOD_TYPES.DAY) {
    fromDate = startOfDay(safeCursor);
    toDate = endOfDay(safeCursor);
  } else if (safeMode === EVALUATION_PERIOD_TYPES.MANUAL) {
    const parsedFrom = parseDateInputValue(manualFrom, safeCursor);
    const parsedTo = parseDateInputValue(manualTo, safeCursor);

    // If user accidentally sets from > to, normalize by swapping.
    if (parsedFrom && parsedTo && parsedFrom.getTime() <= parsedTo.getTime()) {
      fromDate = parsedFrom;
      toDate = parsedTo;
    } else {
      fromDate = parsedTo || safeCursor;
      toDate = parsedFrom || safeCursor;
    }
  }

  return {
    mode: safeMode,
    isManual: safeMode === EVALUATION_PERIOD_TYPES.MANUAL,
    showManualInputs: safeMode === EVALUATION_PERIOD_TYPES.MANUAL,
    from: toDateInputValue(fromDate),
    to: toDateInputValue(toDate),
    fromDate,
    toDate,
    displayLabel: buildEvaluationPeriodLabel({
      mode: safeMode,
      fromDate,
      toDate,
      cursorDate: safeCursor,
    }),
  };
}

/* -------------------------------------------------------------------------- */
/* Cursor navigation                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Move one period backward or forward.
 *
 * direction:
 *   -1 => previous
 *   +1 => next
 */
export function shiftEvaluationCursor({ mode, cursorDate, direction }) {
  const safeMode = normalizeEvaluationPeriodMode(mode);
  const safeCursor = normalizeDate(cursorDate);
  const delta = Number(direction || 0);

  if (safeMode === EVALUATION_PERIOD_TYPES.MONTH) {
    return addMonths(safeCursor, delta);
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.THREE_MONTHS) {
    return addMonths(safeCursor, delta * 3);
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.SIX_MONTHS) {
    return addMonths(safeCursor, delta * 6);
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.YEAR) {
    return addYears(safeCursor, delta);
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.WEEK) {
    return addDays(safeCursor, delta * 7);
  }

  if (safeMode === EVALUATION_PERIOD_TYPES.DAY) {
    return addDays(safeCursor, delta);
  }

  // MANUAL mode has no arrow navigation.
  return safeCursor;
}